import { throttle } from 'throttle-debounce'
import * as React from 'react'

import { captureUniversityRequestError } from 'embed/constants/errors'

import FormAutocomplete, { CommonProps as FormAutocompleteProps } from 'embed/components/FormAutocomplete'
import Api from 'embed/services/Api'

export interface Props extends FormAutocompleteProps {
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onUniversitySelect?: (name: string) => void
}

interface UniversityItem {
  name: string
  key: string
}

const AUTOCOMPLETE_THROTTLE_WAIT = 600

const UniversityAutocomplete = ({ onChange, onUniversitySelect, placeholder, ...props }: Props) => {
  const [items, setItems] = React.useState<UniversityItem[]>([])

  const handleGetItemValue = (item: UniversityItem): string => item.name

  const handleSelect = (itemValue: string) => {
    onUniversitySelect?.(itemValue)
    setItems([])
    return itemValue
  }

  const getUniversityPredictionsThrottled = throttle(AUTOCOMPLETE_THROTTLE_WAIT, (textValue: string) => {
    Api.get(`/leads/universities`, { name: textValue, limit: 20 })
      .then((res) => {
        setItems(res.data.map((name: string): UniversityItem => ({ name, key: name })))
      })
      .catch(captureUniversityRequestError)
  })

  const handleChange = (event: React.FormEvent<HTMLInputElement>, textValue: string) => {
    onChange?.(event, textValue)

    if (textValue) {
      getUniversityPredictionsThrottled(textValue)
    } else {
      setItems([])
    }
  }

  const itemIdentity = (item: UniversityItem) => item.key

  return (
    <FormAutocomplete
      {...props}
      items={items}
      itemId={itemIdentity}
      getItemValue={handleGetItemValue}
      onChange={handleChange}
      onSelect={handleSelect}
      placeholder={placeholder ?? 'Enter university name'}
    />
  )
}

export default UniversityAutocomplete
